import React, { useState, useMemo, useCallback } from "react";
import { synchronizationInProgressVar } from "variables";
import PropTypes from "prop-types";
import { useApolloClient, useSubscription, useReactiveVar } from "@apollo/client";
import MessagesComponent from "./MessagesComponent";
import { useQueryWithSimpleErrorHandling } from "hooks/apolloWrappers";
import { useTranslation } from "react-i18next";
import { MESSAGES_QUERY } from "./queries";
import { REFRESH_MESSAGES } from "./subscriptions";

const MessagesContainer = ({ partnerId, onRetrySynchronization }) => {
  const client = useApolloClient();
  const synchronizationInProgress = useReactiveVar(synchronizationInProgressVar);
  const { t } = useTranslation();
  const [messageActiveTab, setMessagesActiveTab] = useState(false);

  const {
    loading: messagesLoading,
    data: messagesData,
    refetch
  } = useQueryWithSimpleErrorHandling(MESSAGES_QUERY, {
    variables: { partnerId },
    skip: !partnerId
  });

  useSubscription(REFRESH_MESSAGES, {
    variables: { partnerId },
    skip: !partnerId,
    onData: ({ data: { data } }) => {
      clearMessagesCache(data.refreshMessages.partnerId);
      refetch({ partnerId });
    }
  });

  const clearMessagesCache = partnerId => {
    if (!partnerId) return;

    const { cache } = client;
    const cacheKey = Object.keys(cache.data.data).find(x => x.match(`PartnerMessage:{"partnerId":${partnerId}}`));
    if (cacheKey) {
      const normalizedId = cache.identify({ partnerId, __typename: "PartnerMessage" });
      cache.evict({ id: normalizedId });
      cache.gc();
    }
  };

  const handleChange = useCallback(
    newValue => {
      if (messageActiveTab === newValue) {
        setMessagesActiveTab(false);
      } else {
        setMessagesActiveTab(newValue);
      }
    },
    [messageActiveTab]
  );

  const handleMessage = useCallback(
    messageType => {
      switch (messageType) {
        case "Warning":
          return t("MessagesTags.Warnings").toLowerCase();
        case "Error":
          return t("MessagesTags.Errors").toLowerCase();
        case "Info":
          return t("MessagesTags.Info").toLowerCase();
        default:
          return;
      }
    },
    [t]
  );

  const displaySummary = useCallback(
    messageType => {
      let errorMessage = "";

      switch (messageType) {
        case 0:
        case "Info":
          return (errorMessage = t("MessagesTags.Info"));
        case "Warning":
          return (errorMessage = t("MessagesTags.Warnings"));
        case "Error":
          return (errorMessage = t("MessagesTags.Errors"));
        default:
          return errorMessage;
      }
    },
    [t]
  );

  const messageTypeList = useMemo(() => {
    return ["Info", "Warning", "Error"];
  }, []);

  return (
    <>
      {!messagesLoading && messagesData && messagesData.partnerMessages?.messages?.length > 0 && (
        <MessagesComponent
          messages={messagesData.partnerMessages?.messages}
          onChange={handleChange}
          getMessages={handleMessage}
          displaySummary={displaySummary}
          messageTypeList={messageTypeList}
          messageActiveTab={messageActiveTab}
          synchronizationInProgress={synchronizationInProgress}
          onRetrySynchronization={onRetrySynchronization}
        />
      )}
    </>
  );
};

MessagesContainer.propTypes = {
  partnerId: PropTypes.number.isRequired,
  onRetrySynchronization: PropTypes.func
};

export default MessagesContainer;
